import * as React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocialGetPostQuery, socialGetPostQueryKey } from '@web/gen/feed'

import type { Project } from '../page-client'

export interface UseProjectOptions {
  postSlug: string
  initialData?: Project
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
}

export function useProject({
  postSlug,
  enabled = false,
  staleTime = 5 * 60 * 1000, // 5 minutes
  refetchOnMount = false,
  refetchOnWindowFocus = false,
}: UseProjectOptions) {
  const queryClient = useQueryClient()

  const {
    data: queryData,
    isLoading,
    isFetching,
    isError,
    error,
    // refetch,
    isStale,
    dataUpdatedAt,
    fetchStatus,
  } = useSocialGetPostQuery(
    {
      input: { postSlug },
    },
    {
      enabled, // More strict enabling
      staleTime,
      refetchOnMount,
      refetchOnWindowFocus,
      refetchOnReconnect: enabled, // Only reconnect if enabled
    },
  )

  const project = queryData?.socialGetPost?.post || undefined

  // Utility functions
  const invalidate = React.useCallback(() => {
    const queryKey = socialGetPostQueryKey({ input: { postSlug } })
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, postSlug])

  const remove = React.useCallback(() => {
    const queryKey = socialGetPostQueryKey({ input: { postSlug } })
    queryClient.removeQueries({ queryKey })
  }, [queryClient, postSlug])

  // oxlint-disable-next-line require-await
  const refetchProject = React.useCallback(async () => {
    const queryKey = socialGetPostQueryKey({ input: { postSlug } })
    return queryClient.refetchQueries({ queryKey })
  }, [queryClient, postSlug])

  return {
    // Data
    project,
    isLoading,
    isFetching,
    isError,
    error: error as Error | null,

    // Utility functions
    refetch: refetchProject,
    invalidate,
    remove,

    // Query state
    isStale,
    dataUpdatedAt,
    fetchStatus,
  }
}

// Specialized hook for project actions with optimistic updates
export interface UseProjectActionsOptions {
  project: Project
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export interface UseProjectActionsReturn {
  // Vote actions
  toggleLike: (currentlyLiked: boolean) => void
  isVoting: boolean

  // Utility functions
  invalidateProject: () => void
  refetchProject: () => Promise<void>
}

// export function useProjectActions({
//   project,
//   onSuccess,
//   onError,
// }: UseProjectActionsOptions): UseProjectActionsReturn {
//   const queryClient = useQueryClient()

//   // Import the actual mutation hooks
//   const socialFeedPostAddLikeMutation = useSocialFeedPostAddLikeMutation()
//   const socialFeedPostRemoveLikeMutation = useSocialFeedPostRemoveLikeMutation()

//   const { mutate: toggleLike, isPending: isVoting } = useMutation({
//     mutationFn: async (currentlyLiked: boolean) => {
//       return currentlyLiked
//         ? socialFeedPostRemoveLikeMutation.mutateAsync({ input: { postId: Number(project?.id) } })
//         : socialFeedPostAddLikeMutation.mutateAsync({ input: { postId: Number(project?.id) } })
//     },
//     onMutate: async (currentlyLiked: boolean) => {
//       const queryKey = socialGetPostQueryKey({ input: { postSlug: project.postSlug } })

//       await queryClient.cancelQueries({ queryKey })
//       const previousData = queryClient.getQueryData(queryKey)

//       // Optimistic update
//       queryClient.setQueryData(queryKey, (old: any) => {
//         if (!old?.socialGetPost?.post) return old

//         return {
//           ...old,
//           socialGetPost: {
//             ...old.socialGetPost,
//             post: {
//               ...old.socialGetPost.post,
//               isLiked: !currentlyLiked,
//               likesCount: currentlyLiked
//                 ? old.socialGetPost.post.likesCount - 1
//                 : old.socialGetPost.post.likesCount + 1,
//             },
//           },
//         }
//       })

//       return { previousData, queryKey }
//     },
//     onSuccess: () => {
//       onSuccess?.()
//       // Invalidate to get fresh data
//       const queryKey = socialGetPostQueryKey({ input: { postSlug: project.postSlug } })
//       queryClient.invalidateQueries({ queryKey })
//     },
//     onError: (error, _, context) => {
//       onError?.(error)
//       // Revert optimistic update
//       if (context?.previousData && context?.queryKey) {
//         queryClient.setQueryData(context.queryKey, context.previousData)
//       }
//     },
//   })

//   const invalidateProject = React.useCallback(() => {
//     const queryKey = socialGetPostQueryKey({ input: { postSlug: project.postSlug } })
//     queryClient.invalidateQueries({ queryKey })
//   }, [queryClient, project.postSlug])

//   const refetchProject = React.useCallback(async () => {
//     const queryKey = socialGetPostQueryKey({ input: { postSlug: project.postSlug } })
//     return queryClient.refetchQueries({ queryKey })
//   }, [queryClient, project.postSlug])

//   return {
//     toggleLike,
//     isVoting,
//     invalidateProject,
//     refetchProject,
//   }
// }
